import React, { useCallback, useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from '../hooks/useTranslation';
import { KeepAliveBridge } from '../native/KeepAliveBridge';

function PermissionRow({ label, description, status, hasRequested, onOpenSettings, openSettingsLabel }: {
  label: string;
  description: string;
  status: string;
  hasRequested: boolean;
  onOpenSettings: () => void;
  openSettingsLabel: string;
}) {
  const isGranted = status === 'granted';
  // Show "Open Settings" only if we already tried requesting and it's still not granted
  const needsSettings = hasRequested && !isGranted;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      backgroundColor: isGranted ? '#f0fdf4' : needsSettings ? '#fef2f2' : 'var(--caro-card-bg)',
      borderRadius: 12,
      border: `1px solid ${isGranted ? '#bbf7d0' : needsSettings ? '#fecaca' : 'var(--caro-border)'}`,
      marginBottom: 12,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: 600,
          fontSize: 'var(--font-body)',
          color: 'var(--caro-text)',
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 'var(--font-body)',
          color: 'var(--caro-text-light)',
          marginTop: 4,
        }}>
          {description}
        </div>
      </div>
      <div style={{ marginLeft: 16, flexShrink: 0 }}>
        {isGranted ? (
          <span style={{ color: '#16a34a', fontSize: 'var(--font-heading)' }}>&#10003;</span>
        ) : needsSettings ? (
          <button
            onClick={onOpenSettings}
            style={{
              color: '#dc2626',
              fontSize: 'var(--font-body)',
              padding: '6px 12px',
              backgroundColor: '#fef2f2',
              borderRadius: 8,
              border: '1px solid #fecaca',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {openSettingsLabel}
          </button>
        ) : (
          <span style={{ color: '#f59e0b', fontSize: 'var(--font-body)', fontWeight: 500 }}>
            &#9679; Pending
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Full-screen permission gate shown on first launch.
 * Blocks access to the app until all permissions are granted.
 *
 * Flow:
 * 1. Initial check shows which permissions are granted/pending
 * 2. "Allow All" triggers Android's native permission dialogs
 * 3. After request, any still-denied permissions show "Open Settings"
 * 4. When user returns from Settings, app re-checks automatically
 */
export function PermissionGate({ children }: { children: React.ReactNode }) {
  const { permissions, checking, allGranted, requestAll, hasRequested } = usePermissions();
  const { t } = useTranslation();
  const [requesting, setRequesting] = useState(false);

  const handleOpenSettings = useCallback(() => {
    KeepAliveBridge.openAppSettings();
  }, []);

  const handleAllowAll = useCallback(async () => {
    setRequesting(true);
    await requestAll();
    setRequesting(false);
  }, [requestAll]);

  if (checking) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: 'var(--caro-bg)',
        color: 'var(--caro-text-light)',
        fontSize: 'var(--font-body)',
      }}>
        {t.common.loading}
      </div>
    );
  }

  if (allGranted) {
    return <>{children}</>;
  }

  const allDeniedAfterRequest = hasRequested &&
    permissions.microphone !== 'granted' &&
    permissions.camera !== 'granted' &&
    permissions.location !== 'granted' &&
    permissions.notifications !== 'granted';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--caro-bg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    }}>
      <div style={{ maxWidth: 500, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 'var(--font-heading)', marginBottom: 16 }}>&#128274;</div>
          <h1 style={{
            fontSize: 'var(--font-heading)',
            color: 'var(--caro-text)',
            marginBottom: 8,
          }}>
            {t.permissions.title}
          </h1>
          <p style={{
            fontSize: 'var(--font-body)',
            color: 'var(--caro-text-light)',
            lineHeight: 1.5,
          }}>
            {t.permissions.description}
          </p>
        </div>

        <PermissionRow
          label={t.permissions.microphone}
          description={t.permissions.microphoneDesc}
          status={permissions.microphone}
          hasRequested={hasRequested}
          onOpenSettings={handleOpenSettings}
          openSettingsLabel={t.permissions.openSettings}
        />
        <PermissionRow
          label={t.permissions.camera}
          description={t.permissions.cameraDesc}
          status={permissions.camera}
          hasRequested={hasRequested}
          onOpenSettings={handleOpenSettings}
          openSettingsLabel={t.permissions.openSettings}
        />
        <PermissionRow
          label={t.permissions.location}
          description={t.permissions.locationDesc}
          status={permissions.location}
          hasRequested={hasRequested}
          onOpenSettings={handleOpenSettings}
          openSettingsLabel={t.permissions.openSettings}
        />
        <PermissionRow
          label={t.permissions.notifications}
          description={t.permissions.notificationsDesc}
          status={permissions.notifications}
          hasRequested={hasRequested}
          onOpenSettings={handleOpenSettings}
          openSettingsLabel={t.permissions.openSettings}
        />

        {!hasRequested ? (
          <button
            onClick={handleAllowAll}
            disabled={requesting}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: requesting ? '#999' : 'var(--caro-primary)',
              color: '#fff',
              borderRadius: 12,
              fontSize: 'var(--font-button)',
              fontWeight: 600,
              marginTop: 20,
              cursor: requesting ? 'wait' : 'pointer',
              border: 'none',
            }}
          >
            {requesting ? t.common.loading : t.permissions.allowAll}
          </button>
        ) : (
          <>
            {/* After requesting, show "Open Settings" as primary action */}
            <button
              onClick={handleOpenSettings}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'var(--caro-primary)',
                color: '#fff',
                borderRadius: 12,
                fontSize: 'var(--font-button)',
                fontWeight: 600,
                marginTop: 20,
                cursor: 'pointer',
                border: 'none',
              }}
            >
              {t.permissions.openSettings}
            </button>
            <p style={{
              textAlign: 'center',
              fontSize: 'var(--font-body)',
              color: 'var(--caro-text-light)',
              marginTop: 16,
              lineHeight: 1.5,
            }}>
              {t.permissions.deniedHelp}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
